import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { Headphones, ArrowRight } from 'lucide-react';
import { AudioFile } from '../services/api';
import { useUserFiles } from '../contexts/UserFilesContext';

// Temporary kill-switch — set to true to restore the hero once the file-metadata
// fanout (which populates viewCount7d / viewCount24h / viewCountTotal per file) is
// rebuilt. All aggregation + render logic preserved beneath the early return so
// flipping back is one line.
const HERO_ENABLED = false;

const PlaysThisWeekHero: React.FC = () => {
  const reduce = useReducedMotion();
  const { files, loading } = useUserFiles();

  const agg = useMemo(() => {
    let plays7d = 0;
    let plays24h = 0;
    let lifetimePlays = 0;
    let topFile: AudioFile | null = null;
    let topFile7d = 0;
    let activeFileCount = 0;

    for (const f of files) {
      const v7 = f.viewCount7d ?? 0;
      const v24 = f.viewCount24h ?? 0;
      const vTotal = f.viewCountTotal ?? 0;
      plays7d += v7;
      plays24h += v24;
      lifetimePlays += vTotal;
      if (v7 > 0) activeFileCount += 1;
      if (v7 > topFile7d) {
        topFile7d = v7;
        topFile = f;
      }
    }

    return { plays7d, plays24h, activeFileCount, topFile, lifetimePlays };
  }, [files]);

  // Count-up animation — only on first reveal of a non-zero number.
  // Subsequent updates (polling, optimistic delete) snap to the new value
  // so the user doesn't get a fresh dopamine-grab animation every refresh.
  const target = agg.plays7d;
  const count = useMotionValue(0);
  const display = useTransform(count, latest => Math.round(latest).toLocaleString());
  const hasAnimatedOnce = useRef(false);

  useEffect(() => {
    if (reduce || hasAnimatedOnce.current) {
      count.set(target);
      return;
    }
    if (target === 0) {
      // Wait for the first real value before kicking off the one-shot animation.
      return;
    }
    const controls = animate(count, target, {
      duration: 1.2,
      ease: [0.16, 1, 0.3, 1],
    });
    hasAnimatedOnce.current = true;
    return () => controls.stop();
  }, [target, count, reduce]);

  const topTitle = useMemo(() => {
    if (!agg.topFile) return null;
    return agg.topFile.title?.trim() || agg.topFile.filename;
  }, [agg.topFile]);

  // Temporary kill-switch — file-metadata fanout is being rebuilt; viewCount7d /
  // viewCount24h / viewCountTotal will report 0 until the new data path lands, and
  // a "0 plays this week" hero is worse UX than no hero at all. Flip HERO_ENABLED
  // at the top of this file when counts are live. All aggregation + animation logic
  // above is intentionally preserved.
  if (!HERO_ENABLED) return null;

  if (loading) {
    return (
      <div
        className="rounded-2xl border p-8 md:p-10 animate-pulse"
        style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-color)' }}
      >
        <div className="h-3 w-40 bg-neutral-800/60 rounded mb-4" />
        <div className="h-16 w-48 bg-neutral-800/60 rounded mb-3" />
        <div className="h-4 w-64 bg-neutral-800/60 rounded" />
      </div>
    );
  }

  // No data at all — don't render (caller falls back to other surfaces)
  if (agg.lifetimePlays === 0) return null;

  const isQuietWeek = agg.plays7d === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-2xl overflow-hidden backdrop-blur-md border shadow-lg"
      style={{
        background:
          'linear-gradient(135deg, rgba(23,23,23,0.75) 0%, rgba(28,20,18,0.7) 50%, rgba(23,23,23,0.75) 100%)',
        borderColor: 'rgba(255, 72, 0, 0.22)',
        boxShadow: '0 10px 30px -8px rgba(255, 72, 0, 0.18), 0 4px 12px -4px rgba(0, 0, 0, 0.35)',
      }}
    >
      <div className="relative px-6 py-4 md:px-10 md:py-5">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1 min-w-0">
            <p
              className="text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'rgba(255,255,255,0.55)' }}
            >
              Your audio · Last 7 days
            </p>

            {isQuietWeek ? (
              <>
                <p className="text-2xl md:text-3xl font-bold leading-tight tracking-tight text-white/90 mb-1">
                  Quiet week.
                </p>
                <p className="text-sm md:text-base" style={{ color: 'rgba(255,255,255,0.65)' }}>
                  Lifetime total:{' '}
                  <span className="text-soundwave-400 font-semibold">
                    {agg.lifetimePlays.toLocaleString()}
                  </span>{' '}
                  plays.{' '}
                  <Link
                    to="/my-files"
                    className="text-soundwave-400 hover:text-soundwave-300 font-semibold inline-flex items-center gap-1 transition-colors"
                  >
                    Share another link <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </p>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-3 mb-1">
                  <motion.span className="text-5xl md:text-6xl font-bold leading-none tracking-tight text-soundwave-400">
                    {display}
                  </motion.span>
                  <span
                    className="text-base md:text-lg font-medium"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    {agg.plays7d === 1 ? 'play this week' : 'plays this week'}
                  </span>
                </div>

                <p className="text-sm md:text-base" style={{ color: 'rgba(255,255,255,0.65)' }}>
                  Across {agg.activeFileCount}{' '}
                  {agg.activeFileCount === 1 ? 'file' : 'files'}
                  {agg.plays24h > 0 && (
                    <>
                      {' · '}
                      <span className="font-semibold">
                        {agg.plays24h.toLocaleString()}
                      </span>{' '}
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>today</span>
                    </>
                  )}
                  {topTitle && agg.topFile && (
                    <>
                      {' · Top: '}
                      <Link
                        to={`/player/${agg.topFile.id}`}
                        className="text-soundwave-400 hover:text-soundwave-300 font-semibold transition-colors"
                      >
                        &ldquo;{topTitle}&rdquo;
                      </Link>{' '}
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                        ({(agg.topFile.viewCount7d ?? 0).toLocaleString()}{' '}
                        {(agg.topFile.viewCount7d ?? 0) === 1 ? 'play' : 'plays'}
                        {(agg.topFile.viewCount24h ?? 0) > 0 && (
                          <>
                            {' · '}
                            {(agg.topFile.viewCount24h ?? 0).toLocaleString()} today
                          </>
                        )}
                        )
                      </span>
                    </>
                  )}
                </p>
              </>
            )}
          </div>

          <div
            className="hidden sm:flex flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-xl items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,72,0,0.18) 0%, rgba(255,122,0,0.10) 100%)',
              border: '1px solid rgba(255,122,0,0.25)',
            }}
          >
            <Headphones className="w-5 h-5 md:w-6 md:h-6 text-soundwave-400" strokeWidth={2} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PlaysThisWeekHero;
